import React, { useState, useEffect, useMemo } from "react";
import { ROLES, UserRole } from "../constant/roles";
import {
  Card,
  CardHeader,
  CardContent,
  CardFooter,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Plus, Send, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  useInboxQuery,
  useThreadQuery,
  useReplyMessageMutation,
  useMarkReadSingleMutation,
  useUnreadCountQuery,
  useSendEnhancedMessageMutation,
  InboxMessageView,
  ThreadResponse,
  SendDirectMessageRequest,
  SendGroupMessageRequest,
  useUserListByRoleQuery,
} from "../api/CommunicationApi";
import { useAuth } from "../App";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

type RecipientLite = {
  id: string;
  name: string;
  email: string;
  roleInCourse?: UserRole;
};

const formatDateTime = (dateInput: string | Date): string => {
  if (!dateInput) return "";
  const date = new Date(dateInput);
  const timeString = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  const dateString = date.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
  return `${timeString}, ${dateString}`;
};

function NewMessageDialog({
  onSave,
  initialOpen = false,
}: {
  onSave: () => void;
  initialOpen?: boolean;
}) {
  const [open, setOpen] = useState(initialOpen);
  const [searchQuery, setSearchQuery] = useState("");
  const [formData, setFormData] = useState<{
    recipients: string[];
    subject: string;
    content: string;
    isGroupMessage: boolean;
    groupName?: string;
  }>({
    recipients: [],
    subject: "",
    content: "",
    isGroupMessage: false,
    groupName: "",
  });

  const sendMessageMutation = useSendEnhancedMessageMutation();

  // Load recipients
  const {
    data: usersResp,
    isLoading: isUsersLoading,
    isError: isUsersError,
  } = useUserListByRoleQuery({}, true);

  // Derive a flat ordered list: instructors -> tas -> students
  const orderedRecipients: RecipientLite[] = useMemo(() => {
    const data = (usersResp as { data?: any })?.data as {
      instructors?: any[];
      tas?: any[];
      students?: any[];
      users?: any[];
    };

    // If backend already returns grouped shape like in sample, flatten in desired order
    if (data?.instructors || data?.tas || data?.students) {
      const instructors = (data.instructors ?? []).map((u: any) => ({
        id: u.id,
        name: u.name,
        email: u.email,
        roleInCourse: u.roleInCourse ?? ROLES.INSTRUCTOR,
      })) as RecipientLite[];

      const tas = (data.tas ?? []).map((u: any) => ({
        id: u.id,
        name: u.name,
        email: u.email,
        roleInCourse: u.roleInCourse ?? ROLES.TA,
      })) as RecipientLite[];

      const students = (data.students ?? []).map((u: any) => ({
        id: u.id,
        name: u.name,
        email: u.email,
        roleInCourse: u.roleInCourse ?? ROLES.STUDENT,
      })) as RecipientLite[];

      return [...instructors, ...tas, ...students];
    }

    // Otherwise, handle flat API (UserListByRoleResponse.users)
    const flat = (usersResp?.data?.users ?? []) as any[];
    const instructors: RecipientLite[] = [];
    const tas: RecipientLite[] = [];
    const students: RecipientLite[] = [];

    for (const u of flat) {
      const role =
        (u.roleInCourse as UserRole | undefined) ??
        (u.role as UserRole | undefined) ??
        (u.roles?.[0]?.name as UserRole | undefined);

      const item: RecipientLite = {
        id: u.id,
        name: u.name,
        email: u.email,
        roleInCourse: role,
      };

      if (role === ROLES.INSTRUCTOR) instructors.push(item);
      else if (role === ROLES.TA) tas.push(item);
      else students.push(item);
    }

    return [...instructors, ...tas, ...students];
  }, [usersResp]);

  // Filter recipients based on search query
  const filteredRecipients = useMemo(() => {
    if (!searchQuery.trim()) return orderedRecipients;

    const query = searchQuery.toLowerCase();
    return orderedRecipients.filter(
      (contact) =>
        contact.name.toLowerCase().includes(query) ||
        contact.email.toLowerCase().includes(query) ||
        (contact.roleInCourse || "").toLowerCase().includes(query),
    );
  }, [orderedRecipients, searchQuery]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const isGroup = formData.recipients.length > 1;
    let payload: SendDirectMessageRequest | SendGroupMessageRequest;

    if (isGroup) {
      payload = {
        recipientId: formData.recipients,
        groupName: formData?.groupName,
        subject: formData.subject,
        content: formData.content,
      };
    } else {
      payload = {
        recipientId: formData?.recipients[0] || "",
        subject: formData.subject,
        content: formData.content,
      };
    }
    sendMessageMutation.mutate(payload, {
      onSuccess: () => {
        onSave();
        setOpen(false);
        setFormData({
          recipients: [],
          subject: "",
          content: "",
          isGroupMessage: false,
          groupName: "",
        });
        setSearchQuery(""); // Reset search query
      },
    });
  };

  const handleRecipientToggle = (contactId: string) => {
    setFormData((prev) => {
      const newRecipients = prev.recipients.includes(contactId)
        ? prev.recipients.filter((id) => id !== contactId)
        : [...prev.recipients, contactId];
      return {
        ...prev,
        recipients: newRecipients,
        isGroupMessage: newRecipients.length > 1,
      };
    });
  };

  const selectedContacts = useMemo(
    () => orderedRecipients.filter((c) => formData.recipients.includes(c.id)),
    [orderedRecipients, formData.recipients],
  );

  // Handle dialog close to reset search
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setSearchQuery(""); // Reset search when dialog closes
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="bits" className="bg-bits-blue hover:bg-bits-blue/90">
          <Plus className="h-4 w-4 mr-2" />
          New Message
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl bg-white border">
        <DialogHeader>
          <DialogTitle>Compose New Message</DialogTitle>
          <DialogDescription>
            Send a message to instructors, students, or groups.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Recipients</Label>

            {/* Search Field */}
            <div className="relative">
              <Input
                type="text"
                placeholder="Search recipients by name, email, or role..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-gray-100 border-none pl-4 pr-10"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="border rounded-md p-3 max-h-40 overflow-y-auto space-y-2 custom-scroll">
              {isUsersLoading && (
                <div className="text-sm text-muted-foreground">
                  Loading recipients…
                </div>
              )}
              {isUsersError && (
                <div className="text-sm text-red-500">
                  Failed to load recipients. Try again later.
                </div>
              )}
              {!isUsersLoading &&
                !isUsersError &&
                filteredRecipients.length === 0 &&
                searchQuery && (
                  <div className="text-sm text-muted-foreground text-center py-4">
                    No recipients found for "{searchQuery}"
                  </div>
                )}
              {!isUsersLoading &&
                !isUsersError &&
                filteredRecipients.map((contact) => (
                  <div
                    key={contact.id}
                    className="border-b last:border-0 pb-1 border-gray-200  "
                  >
                    <label
                      key={contact.id}
                      className="flex items-start space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                    >
                      <input
                        type="checkbox"
                        checked={formData.recipients.includes(contact.id)}
                        onChange={() => handleRecipientToggle(contact.id)}
                        className="rounded border-gray-300 mt-2 mr-3 cursor-pointer"
                      />
                      <div className="flex-1">
                        <span className="font-medium">{contact.name}</span>
                        <div className="text-xs text-muted-foreground capitalize">
                          {contact.roleInCourse ?? ROLES.STUDENT} &bull;{" "}
                          {contact.email}
                        </div>
                      </div>
                    </label>
                  </div>
                ))}
            </div>

            {selectedContacts.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {selectedContacts.map((contact) => (
                  <Badge
                    key={contact.id}
                    variant="secondary"
                    className="flex items-center space-x-1"
                  >
                    <span>{contact.name}</span>
                    <button
                      type="button"
                      onClick={() => handleRecipientToggle(contact.id)}
                      className="ml-1 hover:bg-gray-300 rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {formData.isGroupMessage && (
            <div className="space-y-2">
              <Label htmlFor="groupName">Group Name</Label>
              <Input
                id="groupName"
                value={formData.groupName}
                onChange={(e) =>
                  setFormData({ ...formData, groupName: e.target.value })
                }
                placeholder="Enter group name..."
                className="bg-gray-100 border-none"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              value={formData.subject}
              onChange={(e) =>
                setFormData({ ...formData, subject: e.target.value })
              }
              placeholder="Message subject..."
              className="bg-gray-100 border-none"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="content">Message</Label>
            <Input
              id="content"
              value={formData.content}
              onChange={(e) =>
                setFormData({ ...formData, content: e.target.value })
              }
              placeholder="Type your message..."
              className="bg-gray-100 border-none"
              required
            />
          </div>

          <div className="flex-1 justify-between items-center">
            <div className="flex w-full space-x-3 mt-4">
              <Button
                type="button"
                className="flex-1"
                variant="outline"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="bits"
                className="flex-1 bg-bits-blue hover:bg-bits-blue/90"
                disabled={
                  formData.recipients.length === 0 ||
                  sendMessageMutation.isPending
                }
              >
                <Send className="h-4 w-4 mr-2" />
                {sendMessageMutation.isPending ? "Sending..." : "Send Message"}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function MessagesTab({
  initialThreadId,
  initialCompose = false,
}: {
  initialThreadId?: string;
  initialCompose?: boolean;
}) {
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(
    initialThreadId ?? null,
  );
  const [replyText, setReplyText] = useState("");
  const { user } = useAuth();
  const userId = user?.id;
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(20);
  const [groupMemberNames, setGroupMemberNames] = useState<string[] | null>(
    null,
  );
  const [receiver, setReceiver] = useState<string | null>(null);
  useEffect(() => {
    if (initialThreadId) setSelectedThreadId(initialThreadId);
  }, [initialThreadId]);

  const tabVisible = true;
  const {
    data: inboxData,
    isLoading: isInboxLoading,
    isError: isInboxError,
    refetch: refetchInbox,
  } = useInboxQuery(
    { page, limit },
    {
      enabled: tabVisible,
      refetchInterval: tabVisible ? 10000 : false,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: true,
      staleTime: 0,
    },
  );

  const threadVisible = !!selectedThreadId;
  const {
    data: threadData,
    isLoading: isThreadLoading,
    isError: isThreadError,
  } = useThreadQuery(selectedThreadId!, {
    enabled: threadVisible,
    refetchInterval: threadVisible ? 10000 : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const { mutate: markRead } = useMarkReadSingleMutation();
  const replyMutation = useReplyMessageMutation();

  const totalItems = useMemo(() => {
    const inbox = inboxData as {
      data?: { messages?: InboxMessageView[]; pagination?: { total?: number } };
    };
    const p = inbox?.data?.pagination;
    if (p?.total != null) return p.total;
    const messages = inbox?.data?.messages ?? [];
    return messages.length;
  }, [inboxData]);

  const totalPages = useMemo(() => {
    const inbox = inboxData as {
      data?: { messages?: InboxMessageView[]; pagination?: { pages?: number } };
    };
    const p = inbox?.data?.pagination;
    if (p?.pages != null) return p.pages;
    return Math.max(1, Math.ceil(totalItems / Math.max(1, limit)));
  }, [inboxData, totalItems, limit]);

  useEffect(() => {
    if (
      !isInboxLoading &&
      (inboxData as any)?.data?.messages &&
      (inboxData as any).data.messages.length > 0 &&
      !selectedThreadId
    ) {
      const firstMessage = (inboxData as any).data.messages[0];
      handleSelectMessage(firstMessage);
    }
  }, [inboxData, isInboxLoading, selectedThreadId, markRead]);

  const handleSelectMessage = (message: InboxMessageView) => {
    if (message.isGroup && message.group?.members) {
      const names = message.group.members.map((member) => {
        if (member.user.id === userId) {
          return "You";
        }
        return member.user.name;
      });
      setGroupMemberNames(names);
      setReceiver(null);
    } else {
      setGroupMemberNames(null);
      setReceiver(message.recipient?.name || null);
    }
    if (message.thread_id !== selectedThreadId) {
      setSelectedThreadId(message.thread_id);

      if (!message.is_read && message.sender?.id !== userId) {
        markRead(message.id);
      }
    }
  };

  const handleAddReply = () => {
    if (!selectedThreadId || !replyText.trim()) return;

    replyMutation.mutate(
      {
        id: selectedThreadId,
        payload: {
          content: replyText,
          subject: `Re: ${(threadData as any)?.data?.[0]?.subject || ""}`,
        },
      },
      {
        onSuccess: () => {
          setReplyText("");
        },
      },
    );
  };

  const handleSaveMessage = () => {
    refetchInbox();
    setPage(1);
  };

  const selectedMessageThread = useMemo<ThreadResponse | undefined>(
    () => threadData?.data,
    [threadData],
  );

  return (
    <Card className="p-4 flex flex-col gap-4 h-[calc(100vh-180px)]">
      <div className="flex items-center justify-between">
        <p className="text-xl font-semibold">
          Messages ({(inboxData as any)?.data?.messages?.length ?? 0})
        </p>
        <NewMessageDialog
          onSave={handleSaveMessage}
          initialOpen={initialCompose}
        />
      </div>

      {isInboxLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <p>Loading messages...</p>
        </div>
      ) : isInboxError ? (
        <div className="flex-1 flex items-center justify-center text-red-500">
          <p>Error loading messages.</p>
        </div>
      ) : (inboxData as any)?.data?.messages?.length > 0 ? (
        <div className="flex flex-row gap-6 flex-1 overflow-hidden">
          {/* LEFT PANE */}
          <div className="w-1/3 min-w-[260px] h-[480px] overflow-hidden border rounded-md flex flex-col justify-between">
            <div className=" overflow-y-auto custom-scroll hover:scrollbar">
              {(inboxData as any).data.messages.map((message) => (
                <Card
                  key={message.id}
                  onClick={() => handleSelectMessage(message)}
                  className={`p-2 rounded-md cursor-pointer transition-colors duration-150 ${
                    selectedThreadId === message.thread_id
                      ? "bg-gray-100"
                      : "hover:bg-gray-50"
                  } border`}
                >
                  <div className="flex items-start">
                    <div className={`flex-1 overflow-hidden`}>
                      <CardTitle className="text-bits-blue-neutral-900 text-base font-semibold line-clamp-1">
                        {message.subject}
                      </CardTitle>
                      <CardDescription className="text-sm text-muted-foreground truncate line-clamp-1 mt-1">
                        {message.isGroup
                          ? `Group: ${message.group?.name}`
                          : `From: ${message.sender?.name}`}
                      </CardDescription>
                    </div>
                    {!message.is_read && message.sender?.id !== userId && (
                      <div className="h-2 w-2 rounded-full bg-red-500 mr-3 mt-2 flex-shrink-0"></div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
            {totalItems > 0 && (
              <div className="flex items-center border-t justify-between p-2 text-xs">
                <span>
                  Page {page} of {Math.max(1, totalPages)} • {totalItems} items
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1 || isInboxLoading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Prev
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= Math.max(1, totalPages) || isInboxLoading}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                  <Select
                    value={String(limit)}
                    onValueChange={(v) => {
                      setLimit(Number(v));
                      setPage(1);
                    }}
                  >
                    <SelectTrigger className="w-28 bg-bits-grey-100">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[10, 20, 30, 50].map((l) => (
                        <SelectItem key={l} value={String(l)}>
                          {l} / page
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT PANE */}
          <div className="w-2/3 grow overflow-hidden">
            <Card className="flex flex-col h-[480px] overflow-hidden">
              {isThreadLoading ? (
                <div className="flex-1 flex items-center justify-center">
                  <p>Loading conversation...</p>
                </div>
              ) : isThreadError ? (
                <div className="flex-1 flex items-center justify-center text-red-500">
                  <p>Error loading conversation.</p>
                </div>
              ) : selectedMessageThread ? (
                <div className="flex flex-col w-full h-full">
                  <CardHeader className="shrink-0 px-4 pt-3">
                    <div>
                      <CardTitle className="!text-bits-blue-neutral-900 text-xl font-semibold">
                        {(selectedMessageThread as any)[0].subject}
                      </CardTitle>
                      <CardDescription className="flex items-center gap-2">
                        {(selectedMessageThread as any)[0].isGroup &&
                        groupMemberNames ? (
                          <p
                            className="text-sm text-muted-foreground line-clamp-1"
                            title={groupMemberNames.join(", ")}
                          >
                            <span className="font-semibold">
                              {" "}
                              Group Members :
                            </span>{" "}
                            {groupMemberNames.join(", ")}
                          </p>
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            <span className="font-semibold">To :</span>{" "}
                            {receiver}
                          </p>
                        )}
                      </CardDescription>
                    </div>
                  </CardHeader>

                  <CardContent className="flex-1 min-h-0 overflow-y-auto px-4 custom-scroll hover:scrollbar">
                    {selectedMessageThread.map((msg: any) => (
                      <div key={msg.id} className="space-y-2">
                        <CardDescription className="flex items-center gap-2">
                          <p className="font-semibold text-base text-bits-grey">
                            {msg.sender?.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatDateTime(msg.createdAt!)}
                          </p>
                        </CardDescription>

                        <Card className="rounded-md w-11/12 p-2 !bg-gray-50">
                          <p className="whitespace-pre-line text-sm text-bits-grey">
                            {msg.content}
                          </p>
                        </Card>
                      </div>
                    ))}
                  </CardContent>

                  <CardFooter className="shrink-0 flex items-center justify-between gap-2 p-4 h-20">
                    <Input
                      type="text"
                      placeholder="Type your Reply"
                      className="h-full bg-gray-100"
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && handleAddReply()}
                    />
                    <Button
                      variant="bits"
                      className="bg-bits-blue text-white font-bold py-2 px-4 rounded-lg h-full"
                      onClick={handleAddReply}
                      disabled={replyMutation.isPending || !replyText.trim()}
                    >
                      <Send className="h-4 w-4 md:mr-2" />
                      <span className="hidden md:inline">
                        {replyMutation.isPending ? "Sending..." : "Send"}
                      </span>
                    </Button>
                  </CardFooter>
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <p>Select a message to view the conversation.</p>
                </div>
              )}
            </Card>
          </div>
        </div>
      ) : (
        <div className="flex-1 mx-6 mt-4 mb-6 flex items-center justify-center">
          <div className="text-center text-muted-foreground">
            <p className="text-lg font-medium">No messages yet</p>
            <p className="mt-1">Create a new message to get started.</p>
          </div>
        </div>
      )}
    </Card>
  );
}
